import * as React from 'react';
import './style.scss';
import { Button, Col, Form, Input, Row, Select, notification } from 'antd';
import UploadMutipleImage from '@components/common/upload/UploadMutipleImage';
import { useAppDispatch, useAppSelector } from '@hook/index';
import { convert } from '@utils/convert';
import TransferApi from '@services/Transfer';
import { deposit, attachReceipt } from '@store/transferSlice';
import { ITransferDeposit } from '@interfaces/Transfer';
import showMessage from '@utils/helper';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { Link, useLocation } from 'react-router-dom';
export interface IDepositPCProps {}

type IBankInfo = { bank_type?: string; owner_name?: string; card_no?: string; bank_logo?: string };

function DepositQrResult(props: {
  transferId?: number;
  qrUrl?: string;
  billNo?: string;
  money?: string;
  payUrl?: string;
  expiresAt?: string;
  createdAt?: string;
  bankInfo?: IBankInfo;
  dispatch: ReturnType<typeof useAppDispatch>;
  onClose: () => void;
}) {
  const { transferId, qrUrl, billNo, money, bankInfo, dispatch, onClose } = props;
  const [now, setNow] = React.useState(Date.now());
  const [qrFailed, setQrFailed] = React.useState(false);
  const parseTime = (value?: string) => {
    if (!value) return NaN;
    // API SQL timestamps are Vietnam local time; never interpret them in the device timezone.
    return Date.parse(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value) ? value.replace(' ', 'T') + '+07:00' : value);
  };
  const expiry = parseTime(props.expiresAt);
  const created = parseTime(props.createdAt);
  const deadline = Number.isFinite(expiry) ? expiry : Number.isFinite(created) ? created + 30 * 60000 : NaN;
  const seconds = Number.isFinite(deadline) ? Math.max(0, Math.ceil((deadline - now) / 1000)) : null;
  const expired = seconds === 0;
  const countdown = seconds === null ? 'Đang xác minh' : expired ? 'Đã hết hạn' : `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
  React.useEffect(() => { const id = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(id); }, []);
  const openPayment = () => {
    if (expired) return;
    const providerUrl = props.payUrl && /^https?:\/\//i.test(props.payUrl) ? props.payUrl : '';
    if (providerUrl) { window.open(providerUrl, '_blank', 'noopener,noreferrer'); return; }
    const popup = window.open('', '_blank');
    if (!popup) { notification.warning({ message: 'Vui lòng cho phép mở tab thanh toán' }); return; }
    popup.opener = null;
    const doc = popup.document;
    doc.title = 'Thông tin thanh toán';
    const meta = doc.createElement('meta'); meta.name = 'viewport'; meta.content = 'width=device-width,initial-scale=1'; doc.head.appendChild(meta);
    const style = doc.createElement('style'); style.textContent = 'body{font:15px system-ui;margin:16px;background:#132345;color:white}main{max-width:440px;margin:auto}img{width:190px;display:block;margin:12px auto}p{padding:8px 0;border-bottom:1px solid #567;overflow-wrap:anywhere}'; doc.head.appendChild(style);
    const main = doc.createElement('main'); doc.body.appendChild(main);
    const heading = doc.createElement('h2'); heading.textContent = 'Thông tin thanh toán'; main.appendChild(heading);
    if (qrUrl && !qrFailed) { const img = doc.createElement('img'); img.src = qrUrl; img.alt = 'QR chuyển khoản'; main.appendChild(img); }
    [['Ngân hàng', bankInfo?.bank_type], ['Tên tài khoản', bankInfo?.owner_name], ['Số tài khoản', bankInfo?.card_no], ['Số tiền', money + ' VNĐ'], ['Nội dung chuyển khoản', billNo]].forEach(([label, value]) => { const row = doc.createElement('p'); row.textContent = `${label}: ${value || ''}`; main.appendChild(row); });
    const time = doc.createElement('p'); main.appendChild(time);
    const update = () => { const left = Number.isFinite(deadline) ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000)) : null; time.textContent = left === null ? 'Thời hạn: đang xác minh' : left === 0 ? 'Đã hết hạn — không chuyển tiền vào lệnh này.' : `Còn lại: ${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`; if (left === 0) main.querySelector('img')?.remove(); };
    update(); const timer = window.setInterval(() => { if (popup.closed) window.clearInterval(timer); else update(); }, 1000);
  };
  const [images, setImages] = React.useState<any[]>([]);
  const [uploading, setUploading] = React.useState(false);

  const handleCopy = async (value?: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      notification.success({ message: 'Đã sao chép', duration: 2 });
    } catch (e) {
      notification.error({ message: 'Không thể sao chép, vui lòng bôi đen và sao chép thủ công', duration: 3 });
    }
  };

  const handleUploadReceipt = () => {
    if (!images.length || !transferId) return;
    setUploading(true);
    dispatch(
      attachReceipt({
        param: { transfer_id: transferId, images },
        callback: () => {
          setUploading(false);
          notification.success({ message: 'Đã gửi biên lai, vui lòng chờ xử lý', duration: 3 });
          onClose();
        },
        error: (err) => {
          setUploading(false);
          notification.error({ message: err || 'Gửi biên lai thất bại', duration: 3 });
        },
      }),
    );
  };

  if (expired) return <div style={{color:'#132345',padding:20}}>Lệnh đã hết hạn. Vui lòng đóng và chọn “Bỏ qua / tạo giao dịch mới”. Không chuyển tiền vào lệnh này.</div>;

  const compactRow = (label: string, value?: string, copy = true, copyValue?: string) => (
    <div className="deposit-compact__row" key={label}>
      <span className="deposit-compact__label">{label}</span>
      <div className="deposit-compact__value"><strong>{value || '—'}</strong>{copy && value ? <button type="button" onClick={() => handleCopy(copyValue || value)}>Sao chép</button> : null}</div>
    </div>
  );
  return (
    <div className="deposit-qr-result deposit-compact" style={{ width: '100%', maxWidth: 420, margin: '0 auto', textAlign: 'left' }}>
      <div className="deposit-compact__header">
        <div className="deposit-compact__bank">{bankInfo?.bank_logo ? <img src={bankInfo.bank_logo} alt={bankInfo.bank_type || 'Ngân hàng'} /> : null}<strong>{bankInfo?.bank_type}</strong></div>
        <div className="deposit-compact__timer" role="timer"><span>Thời gian giao dịch</span><strong>{countdown}</strong></div>
      </div>
      <div className="deposit-compact__qr">
        {!expired && qrUrl && !qrFailed ? <img src={qrUrl} alt="QR nạp tiền" onError={() => setQrFailed(true)} /> : <p>{expired ? 'Lệnh đã hết hạn. Vui lòng tạo lệnh mới, không chuyển tiền vào lệnh này.' : 'Không tải được QR. Vui lòng dùng thông tin chuyển khoản bên dưới.'}</p>}
      </div>
      <button type="button" className="deposit-compact__open" onClick={openPayment} disabled={expired}>Mở trang thanh toán</button>
      <div className="deposit-compact__details">
        {compactRow('Tên tài khoản', bankInfo?.owner_name)}
        {compactRow('Số tài khoản', bankInfo?.card_no)}
        {compactRow('Số tiền', money ? money + ' VNĐ' : undefined, true, money?.replace(/[^0-9]/g, ''))}
        {compactRow('Nội dung chuyển khoản', billNo)}
      </div>
      <p className="deposit-compact__note">Chuyển đúng số tiền và nội dung để được cộng tự động.</p>
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <UploadMutipleImage handleChangeImg={setImages} limit={6} />
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn-confirm"
            disabled={uploading || !images.length}
            onClick={handleUploadReceipt}
            style={{ opacity: uploading || !images.length ? 0.6 : 1 }}
          >
            {uploading ? 'Đang gửi...' : 'Tải biên lai'}
          </button>
        </div>
      </div>
    </div>
  );
}


function depositDeadline(order: any) {
  const parse = (v: any) => !v ? NaN : Date.parse(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(v) ? v.replace(' ', 'T') + '+07:00' : v);
  const expiry = parse(order.expires_at); const created = parse(order.created_at);
  return Number.isFinite(expiry) ? expiry : Number.isFinite(created) ? created + 1800000 : NaN;
}
function PendingDepositCard({order, onSkip, onContinue}: {order: any; onSkip: () => void; onContinue: () => void}) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t);}, []);
  const expired = depositDeadline(order) <= now;
  return <section className="pending-gateway-deposit" style={{position:'relative',width:'100%',maxWidth:440,boxSizing:'border-box',padding:22,margin:'12px auto',color:'#eee',background:'#332e22',borderRadius:10,textAlign:'left'}}>
    <button type="button" aria-label="Bỏ qua giao dịch" onClick={onSkip} style={{position:'absolute',right:12,top:8,color:'white',background:'transparent',border:0,fontSize:24}}>×</button>
    <h3 style={{color:'#efb62e',fontSize:20,margin:'0 20px 16px 0'}}>⏳ Giao dịch chưa xử lý</h3>
    <p>{expired ? 'Giao dịch đã hết thời gian thanh toán. Không chuyển tiền vào lệnh này. Bạn có thể bỏ qua để tạo giao dịch mới.' : 'Bạn có một giao dịch chưa hoàn tất. Vui lòng tiếp tục thanh toán hoặc bỏ qua.'}</p>
    <small>Mã giao dịch:</small><p style={{fontWeight:700,overflowWrap:'anywhere'}}>{order.bill_no}</p>
    <small>Số tiền nạp:</small><p style={{fontWeight:700}}>{convert.moneyInput(order.money)} VND</p>
    <small>Ngày tạo:</small><p>{order.created_at || 'Đang cập nhật'}</p>
    <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
      <button type="button" onClick={onSkip} style={{flex:1,minWidth:130,padding:12,border:'1px solid #aaa',borderRadius:6,background:'transparent',color:'white'}}>Bỏ qua / tạo giao dịch mới</button>
      {!expired ? <button type="button" onClick={onContinue} style={{flex:1,minWidth:130,padding:12,border:0,borderRadius:6,background:'#a27a3c',color:'white'}}>Tiếp tục thanh toán</button> : null}
    </div>
    {!expired ? <button type="button" onClick={onContinue} style={{width:'100%',padding:12,marginTop:10,border:0,borderRadius:6,background:'#a27a3c',color:'white'}}>Mở trang giao dịch</button> : null}
  </section>;
}

export default function DepositPC(props: IDepositPCProps) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [form] = Form.useForm();
  const { data } = useAppSelector((state) => state.authSlice);
  const { data: DataHome } = useAppSelector((state) => state.homeSlice);
  const { data: DataBank } = useAppSelector((state) => state.bankAdminSlice);
  const { data: DataGame } = useAppSelector((state) => state.gameSlice);
  const { data: DateEvent } = useAppSelector((state) => state.eventSlice);
  const [methodKey, setMethodKey] = React.useState<number>(0);
  // OPENCLAW-ANPAY-WALLET: phương thức "Ví điện tử" (card_type 4) qua Anpay — khách chọn ví
  const [wallet, setWallet] = React.useState<string>('momo');
  const MySwal = withReactContent(Swal);
  const [dataSubmit, setDataSubmit] = React.useState<Partial<ITransferDeposit>>({
    transfer_type: 0,
  });

  const skippedPendingBills = () => {
    try { return JSON.parse(localStorage.getItem("skippedGatewayTransactions") || "[]").filter((x: any) => typeof x === "string").slice(-50); } catch { return []; }
  };
  // OPENCLAW-VIVAQR: thứ tự kênh do admin chỉnh (API trả order); mặc định Viva QR101 → QR301 → QR201
  const methodEntries = React.useMemo(() => {
    const apiOrder: string[] = Array.isArray(DataBank.methodOrder) ? DataBank.methodOrder.map(String) : ['1', '5', '3'];
    const order = (k: string) => {
      const i = apiOrder.indexOf(k);
      return i >= 0 ? i : 100 + Number(k);
    };
    return Object.entries(DataBank.method || {}).sort((a, b) => order(a[0]) - order(b[0]));
  }, [DataBank.method, DataBank.methodOrder]);
  // OPENCLAW-2026-10-20 (Long #11366): giao dịch chưa xử lý chỉ xét theo đúng cổng member đang chọn.
  const currentCardType = Number(methodEntries[methodKey]?.[0]);
  const [pending, setPending] = React.useState<any>(null);
  React.useEffect(() => {
    let live = true;
    const cardType = Number.isFinite(currentCardType) ? currentCardType : undefined;
    const refresh = () => TransferApi.pending({skip_pending_bills: skippedPendingBills(), card_type: cardType}).then((r) => { if (live) setPending(r.data); }).catch(() => {});
    refresh();
    const timer = window.setInterval(refresh, 60000);
    return () => { live = false; window.clearInterval(timer); };
  }, [currentCardType]);
  const resumePending = (extra: any) => {
    if (depositDeadline(extra) <= Date.now()) { showPending(extra); return; }
    if (extra.qr_url) MySwal.fire({ width: 460, html: <DepositQrResult transferId={extra.transfer_id} qrUrl={extra.qr_url} payUrl={extra.pay_url} expiresAt={extra.expires_at} createdAt={extra.created_at} billNo={extra.transfer_content || extra.bill_no} money={convert.moneyInput(extra.money)} bankInfo={extra.bank_info} dispatch={dispatch} onClose={() => MySwal.close()} />, confirmButtonText: 'Đóng' });
    else if (extra.pay_url) MySwal.fire({ width: 560, html: <iframe title="Thanh toán" src={extra.pay_url} style={{width:'100%',height:600,border:0}} />, confirmButtonText: 'Đóng' });
    else MySwal.fire({ title: 'Giao dịch chưa xử lý', text: `Mã giao dịch: ${extra.bill_no}. Vui lòng xem lịch sử nạp tiền.`, confirmButtonText: 'Đóng' });
  };

  const skipPending = (order: any = pending) => {
    if (!order?.bill_no) return;
    localStorage.setItem("skippedGatewayTransactions", JSON.stringify(Array.from(new Set([...skippedPendingBills(), order.bill_no])).slice(-50)));
    setPending(null);
    TransferApi.pending({skip_pending_bills: skippedPendingBills(), card_type: Number.isFinite(currentCardType) ? currentCardType : undefined}).then((r) => setPending(r.data)).catch(() => {});
  };
  const showPending = (order: any) => {
    setPending(order);
    MySwal.fire({width:460,padding:10,html:<PendingDepositCard order={order} onSkip={() => {skipPending(order); MySwal.close();}} onContinue={() => resumePending(order)} />,showConfirmButton:false,showCloseButton:true});
  };
  const handleResetFile = () => {
    form.resetFields();
  };

  const iconByCardType: Record<string, string> = {
    '1': 'fa-solid fa-building-columns',
    '2': 'fa-solid fa-mobile-screen-button',
    '3': 'fa-solid fa-qrcode',
    '4': 'fa-solid fa-wallet', // OPENCLAW-ANPAY-WALLET
    '5': 'fa-solid fa-qrcode', // OPENCLAW-SHARKPAY
    '6': 'fa-solid fa-arrow-up-right-from-square', // OPENCLAW-MINIPAY
  };


  const typeEvent = location.state
    ? DateEvent.listEvent.filter((item) => item.apply_type === location.state?.typeEvent)
    : DateEvent.listEvent.filter((item) => item.apply_type === 3);

  const handleSubmit = (event: ITransferDeposit) => {
    // OPENCLAW-2026-10-20 (Long #11366): chỉ chặn bằng thẻ "Giao dịch chưa xử lý" khi số tiền vừa nhập
    // TRÙNG với lệnh tồn (cùng cổng, do effect polling đã lọc sẵn). Đổi số tiền khác -> cho tạo lệnh mới.
    const enteredMoney = parseFloat((event.money || '').toString().replace(/,/g, ''));
    if (pending && Number.isFinite(enteredMoney) && Math.abs(Number(pending.money) - enteredMoney) < 0.01) {
      showPending(pending);
      return;
    }
    setDataSubmit({ ...dataSubmit, ...event });
    const pointValue = parseFloat(parseFloat(event.point.toString().replace(/,/g, '')).toFixed(2));
    dispatch(
      deposit({
        param: {
          ...dataSubmit,
          skip_pending_bills: skippedPendingBills(),
          ...event,
          // OPENCLAW-1DPAY: gửi đúng mã phương thức theo cấu hình (trước đây index+1 lệch khi danh sách có khoảng trống)
          card_type: Number(methodEntries[methodKey]?.[0]) || 1,
          ...(Number(methodEntries[methodKey]?.[0]) === 4 ? { wallet } : {}), // OPENCLAW-ANPAY-WALLET
          point: pointValue,
        },
        callback: (messages, extra) => {
          if (extra && depositDeadline(extra) <= Date.now()) {
            showPending(extra);
          } else if (extra?.qr_url) {
            MySwal.fire({
              width: 460,
              padding: '10px 10px',
              html: (
                <DepositQrResult
                  transferId={extra.transfer_id}
                  qrUrl={extra.qr_url} payUrl={extra.pay_url} expiresAt={extra.expires_at} createdAt={extra.created_at}
                  billNo={extra.transfer_content || extra.bill_no}
                  money={extra.money !== undefined ? convert.moneyInput(extra.money) : event.money?.toString()}
                  bankInfo={extra.bank_info}
                  dispatch={dispatch}
                  onClose={() => MySwal.close()}
                />
              ),
              showConfirmButton: true,
              confirmButtonText: 'Đóng',
              customClass: { confirmButton: 'btn-confirm' },
            });
          } else if (extra?.pay_url) {
            // OPENCLAW-MINIPAY: Minipay không có QR/STK — mở trang thanh toán của cổng, khách tự hoàn tất.
            window.open(extra.pay_url, '_blank');
            MySwal.fire({
              title: 'Đã tạo lệnh nạp',
              text: 'Vui lòng hoàn tất thanh toán ở tab vừa mở. Tiền sẽ được cộng tự động sau khi chuyển khoản thành công.',
              icon: 'info',
              confirmButtonText: 'OK',
              customClass: { confirmButton: 'btn-confirm' },
            });
          } else {
            MySwal.fire({
              title: messages,
              confirmButtonText: 'OK',
              customClass: { confirmButton: 'btn-confirm' },
            });
          }
          TransferApi.pending({skip_pending_bills: skippedPendingBills(), card_type: Number.isFinite(currentCardType) ? currentCardType : undefined}).then((r) => setPending(r.data)).catch(() => {});
          handleResetFile();
        },
        error: (error) => {
          MySwal.fire({
            title: `Có lỗi!`,
            text: error,
            icon: 'error',
            confirmButtonText: 'OK',
            customClass: { confirmButton: 'btn-confirm' },
          });
        },
      }),
    );
  };

  const changeMethod = (event, index: number) => {
    event.preventDefault();
    setMethodKey(index);
  };

  const handleChange = (event) => {
    if (event.money) {
      const money = event.money.toString().replace(/,/g, '');
      form.setFieldsValue({
        ...event,
        money: convert.moneyInputs(money),
        point: convert.numWithCommas(money.replace(/\./g, '').replace(',', '') / Number(data?.point_price)),
      });
    } else if (event.point) {
      const point = event.point.toString().replace(/,/g, '');
      form.setFieldsValue({
        ...event,
        money: convert.moneyInputs(point * Number(data?.point_price)),
        point: convert.numWithCommas(point),
      });
    }
  };

  return (
    <div className="deposit-member">
      <Form
        name="basic"
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 24 }}
        onFinish={handleSubmit}
        onValuesChange={handleChange}
        autoComplete="off"
      >
        <div className="rightModule">
          <div className="infoModule">
            <div className="inside-title icon icon-icon32px_deposit">
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i style={{ fontSize: '30px' }} className="fa-solid fa-money-check-dollar"></i>Nạp tiền
              </span>
              <div>Bạn có thể chọn mục nạp tiền phù hợp nhất từ những lựa chọn bên dưới:</div>
            </div>
            {pending ? <PendingDepositCard order={pending} onSkip={() => skipPending(pending)} onContinue={() => resumePending(pending)} /> : null}
            <div className="subModuleListColumn">
              <div className="subModuleTitle">
                <span>
                  <i className="txt-blue">*</i>
                </span>
                Phương thức nạp tiền
              </div>
              <div className="subModule">
                <div>
                  {methodEntries.map(([cardType, item], index) => (
                    <button
                      key={item}
                      className={(methodKey === index && 'active') || ''}
                      onClick={(e) => changeMethod(e, index)}
                    >
                      <div className="icon"><i className={`main-icon ${iconByCardType[cardType] || 'fa-solid fa-wallet'}`}></i></div>
                      <div className="bank">
                        <div>{item}</div>
                        {/* <div>Thời gian thực hiện ：5 phút</div> */}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              {methodEntries[methodKey]?.[0] === '4' ? (
                <>
                  {/* OPENCLAW-ANPAY-WALLET: chọn ví MoMo / ZaloPay / Viettel Money */}
                  <div className="subModuleTitle">
                    <span>
                      <i className="txt-blue">*</i>
                    </span>
                    Chọn ví điện tử
                  </div>
                  <div className="subModule">
                    <div>
                      {[['momo', 'MoMo'], ['zalo', 'ZaloPay'], ['viettel', 'Viettel Money']].map(([code, label]) => (
                        <button
                          key={code}
                          type="button"
                          className={(wallet === code && 'active') || ''}
                          onClick={(e) => { e.preventDefault(); setWallet(code); }}
                        >
                          <div className="icon"><i className="main-icon fa-solid fa-mobile-screen-button"></i></div>
                          <div className="bank">
                            <div>{label}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}
              <div className="subModuleTitle">
                <span>
                  <i className="txt-blue">*</i>Trò chơi
                </span>
              </div>
              <div className="subModule">
                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                  <Form.Item label="" name="game_id" rules={[{ required: true, message: 'Vui lòng chọn trò chơi' }]}>
                    <Select
                      allowClear
                      showSearch
                      placeholder="Chọn trò chơi"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={DataGame.listGame?.map((item) => ({ value: item.id, label: item.title }))}
                    />
                  </Form.Item>
                </Col>
              </div>
              <div className="subModuleTitle">
                <span>
                  <i className="txt-blue">*</i>
                  {`Số tiền (Đơn vị là 1 = ${convert.moneyInput(data?.point_price)} VNĐ). `}
                  {`Vui lòng nhập số điểm hoặc số tiền là số chẵn`}
                </span>
              </div>
              <div className="subModule">
                <Row gutter={10} className="row-form" justify="start">
                  <Col span={10}>
                    <Form.Item
                      label=""
                      name="point"
                      rules={[
                        { required: true, message: 'Vui lòng nhập số điểm' },
                        {
                          message: 'Số điểm phải lớn hơn hoặc bằng 1',
                          validator: (_, value) => {
                            if (Number(parseInt((value === '' ? 0 : value).replace(/\./g, '').replace(',', ''))) >= 1) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject('Số điểm phải lớn hơn hoặc bằng 1');
                            }
                          },
                        },
                      ]}
                    >
                      <Input placeholder="Mua điểm" />
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      label=""
                      name="money"
                      rules={[
                        { required: true, message: 'Vui lòng nhập số tiền' },
                        {
                          message: 'Số tiền phải lớn hơn hoặc bằng ' + convert.moneyInput(data?.point_price),
                          validator: (_, value) => {
                            if (
                              Number(parseInt((value === '' ? 0 : value).toString().replace(/,/g, ''))) >=
                              Number(data?.point_price)
                            ) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(
                                `Số tiền phải lớn hơn hoặc bằng ${convert.moneyInput(data?.point_price)}`,
                              );
                            }
                          },
                        },
                      ]}
                    >
                      <Input placeholder="Số tiền" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
              <div className="subModuleTitle">
                <span>
                  <i className="txt-blue" />
                  Chọn khuyến mãi
                </span>
              </div>
              <div className="subModule">
                <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                  <Form.Item label="" name="event_id">
                    <Select
                      allowClear
                      showSearch
                      placeholder="Chọn sự kiện"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={(typeEvent || DateEvent.listEvent).map((item) => ({
                        value: item.id,
                        label: item.title,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </div>
              <div className="btnGroup">
                <Button htmlType="submit" className="darkGray">
                  Nạp tiền
                </Button>
              </div>
            </div>
          </div>

          <a className="icon icon-books" href="/vn/depositHistory" />
        </div>
      </Form>
    </div>
  );
}
